import { Box, Stack, Tooltip, Typography } from '@mui/material';

export type CmgRange = {
  cmg?: string | null;
  min?: number | null;
  max?: number | null;
};

export type RecommendedRangeData = {
  hasMds: boolean;
  isPdpm: boolean;
  source?: string | null;
  ptOt?: CmgRange;
  slp?: CmgRange;
  flat?: CmgRange;
};

const DASH = '—';

const formatRange = (range?: CmgRange): string => {
  if (range?.min == null || range?.max == null) return DASH;
  return range.min === range.max ? `${range.min}` : `${range.min}–${range.max}`;
};

const isUnassessed = (range?: CmgRange) =>
  !range?.cmg && !(range?.min != null && range?.max != null);

const DisciplineTag = ({ label, primary }: { label: string; primary?: boolean }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      px: 0.75,
      py: '1px',
      borderRadius: '6px',
      fontSize: '10px',
      fontWeight: 700,
      lineHeight: 1.7,
      minWidth: '38px',
      textAlign: 'center',
      color: '#6C737F',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
);

const RangeLine = ({
  label,
  primary,
  range,
}: {
  label?: string;
  primary?: boolean;
  range?: CmgRange;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={0.75}
    sx={{ whiteSpace: 'nowrap' }}
  >
    {label ? (
      <DisciplineTag
        label={label}
        primary={primary}
      />
    ) : null}
    {range?.min != null && range?.max != null ? (
      <>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: '#4F46E5' }}
        >
          {range?.cmg || DASH}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: '#384250' }}
        >
          {formatRange(range)}
        </Typography>
      </>
    ) : (
      <Typography
        variant="caption"
        sx={{ color: '#384250' }}
      >
        {DASH}
      </Typography>
    )}
  </Stack>
);

const RecommendedRangeHeader = ({ data }: { data?: RecommendedRangeData }) => {
  const noAssessment = data?.isPdpm
    ? isUnassessed(data?.ptOt) && isUnassessed(data?.slp)
    : isUnassessed(data?.flat);

  return (
    <Tooltip
      title="Recommended weekly range (min/week)"
      placement="bottom"
      arrow
    >
      <Box sx={{ flexShrink: 0, cursor: 'default' }}>
        <Stack gap={0.25}>
          {data?.isPdpm ? (
            <>
              <RangeLine
                label="PT+OT"
                primary
                range={data?.ptOt}
              />
              <RangeLine
                label="SLP"
                range={data?.slp}
              />
            </>
          ) : (
            <RangeLine range={data?.flat} />
          )}
          {noAssessment ? (
            <Typography
              variant="caption"
              sx={{ color: '#9DA4AE', whiteSpace: 'nowrap' }}
            >
              (No MDS Assessment)
            </Typography>
          ) : data?.source ? (
            <Typography
              variant="caption"
              sx={{ color: '#9DA4AE', whiteSpace: 'nowrap' }}
            >
              (source: {data?.source})
            </Typography>
          ) : null}
        </Stack>
      </Box>
    </Tooltip>
  );
};

export default RecommendedRangeHeader;
